import React, { useCallback, useEffect, useState } from 'react';
import {
  FiChevronDown, FiChevronRight,
  FiMaximize2, FiMinimize2, FiBriefcase, FiDollarSign, FiTrendingDown,
  FiTrendingUp, FiLayers,
} from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { showError } from '../../utils/alert';
import PageHeader from '../../components/common/PageHeader';
import Loader from '../../components/common/Loader';

const CATEGORY_META = {
  Assets: { icon: <FiBriefcase />, color: '#10b981', bg: '#ecfdf5', border: '#a7f3d0' },
  Liabilities: { icon: <FiDollarSign />, color: '#ef4444', bg: '#fef2f2', border: '#fecaca' },
  Equity: { icon: <FiLayers />, color: '#8b5cf6', bg: '#f5f3ff', border: '#ddd6fe' },
  Income: { icon: <FiTrendingUp />, color: '#06b6d4', bg: '#ecfeff', border: '#a5f3fc' },
  Expenses: { icon: <FiTrendingDown />, color: '#f59e0b', bg: '#fffbeb', border: '#fde68a' },
};

function countAll(nodes) {
  let c = 0;
  nodes.forEach((n) => { c++; if (n.children) c += countAll(n.children); });
  return c;
}

export default function ChartOfAccounts() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [allExpanded, setAllExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.accounts.tree({ operation: 'view-accounts' });
      setCategories(data.accounts || []);
    } catch (err) {
      showError('Failed to load chart of accounts', extractError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const toggleAll = () => {
    if (allExpanded) { setExpanded({}); }
    else {
      const next = {};
      const walk = (nodes, prefix) => {
        nodes.forEach((n) => {
          const k = prefix + n.id;
          if (n.children && n.children.length > 0) {
            next[k] = true;
            walk(n.children, k + '-');
          }
        });
      };
      walk(categories, 'cat-');
      setExpanded(next);
    }
    setAllExpanded((v) => !v);
  };

  const renderNode = (node, level = 0) => {
    const key = `node-${node.id}`;
    const open = expanded[key];
    const children = node.children || [];
    const canHaveChildren = children.length > 0;

    return (
      <div key={node.id}>
        <div
          className={`coa-node ${!canHaveChildren ? 'coa-node--leaf' : 'coa-node--parent'}`}
          style={{ paddingLeft: level * 24 }}
          onClick={!canHaveChildren ? undefined : () => toggle(key)}
        >
          <span className={`coa-caret ${!canHaveChildren ? 'leaf' : ''}`}>
            {!canHaveChildren ? null : open ? <FiChevronDown /> : <FiChevronRight />}
          </span>
          <span className={`coa-dot ${!canHaveChildren ? 'leaf-dot' : ''}`} />
          <span className="coa-name">{node.name}</span>
          {node.account_nature && canHaveChildren === false && (
            <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 6 }}>({node.account_nature})</span>
          )}
          {canHaveChildren && <span className="coa-chip">{children.length} items</span>}
        </div>
        {open && canHaveChildren && (
          <div className="coa-branch">
            {children.map((child) => renderNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const totalAccounts = countAll(categories);

  return (
    <div className="page">
      <PageHeader
        title="Chart of Accounts"
        subtitle="Accounts organized by category — Assets, Liabilities, Equity, Income & Expenses."
        crumbs={[{ label: 'Finance' }, { label: 'Chart of Accounts' }]}
      />

      <div className="card">
        <div className="coa-toolbar">
          <div className="coa-stats">
            <span className="coa-stat"><FiBriefcase /> <b>{categories.length}</b> Types</span>
            <span className="coa-stat"><FiLayers /> <b>{totalAccounts}</b> Accounts</span>
          </div>
          <div className="coa-actions">
            <button className={`coa-expand-btn ${allExpanded ? 'on' : ''}`} onClick={toggleAll}>
              {allExpanded ? <FiMinimize2 /> : <FiMaximize2 />}
              {allExpanded ? 'Collapse All' : 'Expand All'}
            </button>
          </div>
        </div>

        <div className="coa-body">
          {loading ? (
            <Loader text="Loading chart of accounts..." />
          ) : categories.length === 0 ? (
            <div className="empty-state">
              <h4>No accounts yet</h4>
              <p>Start by adding accounts from Account Settings.</p>
            </div>
          ) : (
            <div className="coa-tree">
              {categories.map((type) => {
                const meta = CATEGORY_META[type.name] || { icon: <FiLayers />, color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb' };
                const catKey = `cat-${type.id}`;
                const catOpen = expanded[catKey];
                const children = type.children || [];
                const childCount = countAll(children);

                return (
                  <div key={type.id} className="coa-category">
                    <div
                      className="coa-category-header"
                      style={{
                        background: meta.bg,
                        borderColor: meta.border,
                        color: meta.color,
                      }}
                      onClick={() => toggle(catKey)}
                    >
                      <span className="coa-category-caret">
                        {catOpen ? <FiChevronDown /> : <FiChevronRight />}
                      </span>
                      <span className="coa-category-icon" style={{ color: meta.color, background: meta.border }}>
                        {meta.icon}
                      </span>
                      <span className="coa-category-name">{type.name}</span>
                      <span className="coa-category-count" style={{ color: meta.color, background: meta.border }}>
                        {childCount} accounts
                      </span>
                    </div>
                    {catOpen && (
                      <div className="coa-category-body">
                        {children.length === 0 ? (
                          <div className="coa-empty">No sub-accounts yet</div>
                        ) : (
                          children.map((child) => renderNode(child, 0))
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
